import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const loadUserInfo = async () => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    setIsLoggedIn(hasToken);

    if (!hasToken) {
      setUserInfo({ firstName: '', lastName: '', email: '' });
      return;
    }

    // Try reading cached user info from localStorage
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.firstName || parsed.first_name || parsed.email) {
          setUserInfo({
            firstName: parsed.firstName || parsed.first_name || '',
            lastName: parsed.lastName || parsed.last_name || '',
            email: parsed.email || '',
          });
          return;
        }
      }
    } catch (_) {}

    // Fetch from backend API if not in localStorage
    try {
      const res = await fetch('https://best3intennis.com/best3intennis-api/api/get_account_info.php', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const userObj = {
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            email: data.email || '',
          };
          setUserInfo(userObj);
          localStorage.setItem('user', JSON.stringify(userObj));
        }
      }
    } catch (err) {
      console.warn('Could not fetch user profile for header:', err);
    }
  };

  useEffect(() => {
    loadUserInfo();

    const handleStorageChange = () => {
      loadUserInfo();
    };

    const handleUserUpdate = () => {
      loadUserInfo();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  const getInitials = () => {
    const first = (userInfo.firstName || '').trim();
    const last = (userInfo.lastName || '').trim();
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (first) {
      return first.substring(0, Math.min(2, first.length)).toUpperCase();
    }
    if (userInfo.email) {
      return userInfo.email.trim().charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    const first = (userInfo.firstName || '').trim();
    const last = (userInfo.lastName || '').trim();
    if (first || last) {
      return `${first} ${last}`.trim();
    }
    if (userInfo.email) {
      return userInfo.email;
    }
    return 'My Account';
  };

  const initials = getInitials();
  const displayName = getDisplayName();

  return (
    <header className="sticky top-0 bg-gradient-to-r from-green-500 to-yellow-400 text-white py-3 shadow-md border-b border-gray-300 z-50">
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Left: Brand Logo */}
        <NavLink to={isLoggedIn ? "/dashboard" : "/"} className="flex items-center gap-2 focus:outline-none">
          <img src="/Images/logo1.png" alt="Best3inTennis Logo" className="h-10 sm:h-12 w-auto" />
        </NavLink>

        {/* Center: Regular Website Navigation Menu (Desktop Only) */}
        <nav className="hidden md:flex items-center space-x-5 lg:space-x-6">
          <NavLink
            to="/mental-toughness-book"
            className={({ isActive }) =>
              `font-medium transition duration-200 py-1 px-2 rounded ${
                isActive ? 'text-green-950 bg-white/30 shadow-xs font-semibold' : 'text-white hover:text-yellow-100'
              }`
            }
          >
            Mental Toughness Book
          </NavLink>
          <NavLink
            to="/log-book-hub"
            className={({ isActive }) =>
              `font-medium transition duration-200 py-1 px-2 rounded ${
                isActive ? 'text-green-950 bg-white/30 shadow-xs font-semibold' : 'text-white hover:text-yellow-100'
              }`
            }
          >
            Log Books
          </NavLink>
          <NavLink
            to="/online-match-tracking"
            className={({ isActive }) =>
              `font-medium transition duration-200 py-1 px-2 rounded ${
                isActive ? 'text-green-950 bg-white/30 shadow-xs font-semibold' : 'text-white hover:text-yellow-100'
              }`
            }
          >
            Online Match Tracking
          </NavLink>
          <NavLink
            to="/extras-shop"
            className={({ isActive }) =>
              `font-medium transition duration-200 py-1 px-2 rounded ${
                isActive ? 'text-green-950 bg-white/30 shadow-xs font-semibold' : 'text-white hover:text-yellow-100'
              }`
            }
          >
            Shop
          </NavLink>
          <NavLink
            to="/mental-edge-blog"
            className={({ isActive }) =>
              `font-medium transition duration-200 py-1 px-2 rounded ${
                isActive ? 'text-green-950 bg-white/30 shadow-xs font-semibold' : 'text-white hover:text-yellow-100'
              }`
            }
          >
            Blog
          </NavLink>
        </nav>

        {/* Right: Actions */}
        <div className="flex items-center space-x-3">
          {isLoggedIn ? (
            /* User Initials Bubble / Avatar Dropdown Menu (Only menu on mobile for logged-in users) */
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 p-1 pl-1.5 pr-2.5 rounded-full bg-white/20 hover:bg-white/30 border border-white/40 shadow-sm transition duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 cursor-pointer"
                aria-haspopup="true"
                aria-expanded={isUserMenuOpen}
                aria-label="User menu"
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white text-green-700 font-extrabold flex items-center justify-center text-xs sm:text-sm shadow-inner uppercase tracking-wider select-none">
                  {initials}
                </div>
                <span className="hidden lg:inline-block text-xs font-bold text-white max-w-[120px] truncate select-none">
                  {displayName}
                </span>
                <svg
                  className={`w-3.5 h-3.5 text-white transition-transform duration-200 ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Bubble Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 max-h-[85vh] overflow-y-auto bg-white text-gray-800 rounded-xl shadow-2xl border border-gray-200 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  {/* User Profile Card */}
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-600 to-green-700 text-white font-bold flex items-center justify-center text-sm shadow-sm select-none uppercase flex-shrink-0">
                        {initials}
                      </div>
                      <div className="overflow-hidden">
                        <div className="text-xs font-bold text-gray-900 truncate">
                          {displayName}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {userInfo.email || 'Logged in'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Logged-In Menu Items (Visible on all resolutions) */}
                  <div className="py-1">
                    <div className="px-4 py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider md:hidden">
                      App Menu
                    </div>

                    <NavLink
                      to="/dashboard"
                      onClick={() => setIsUserMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition duration-150 ${
                          isActive
                            ? 'bg-green-50 text-green-700 font-bold'
                            : 'text-gray-700 hover:bg-gray-100 font-medium'
                        }`
                      }
                    >
                      <span className="text-base leading-none">🏠</span>
                      <span>Dashboard</span>
                    </NavLink>

                    <NavLink
                      to="/stats"
                      onClick={() => setIsUserMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition duration-150 ${
                          isActive
                            ? 'bg-green-50 text-green-700 font-bold'
                            : 'text-gray-700 hover:bg-gray-100 font-medium'
                        }`
                      }
                    >
                      <span className="text-base leading-none">📊</span>
                      <span>Stats</span>
                    </NavLink>

                    <NavLink
                      to="/history"
                      onClick={() => setIsUserMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition duration-150 ${
                          isActive
                            ? 'bg-green-50 text-green-700 font-bold'
                            : 'text-gray-700 hover:bg-gray-100 font-medium'
                        }`
                      }
                    >
                      <span className="text-base leading-none">📋</span>
                      <span>History</span>
                    </NavLink>

                    <NavLink
                      to="/account"
                      onClick={() => setIsUserMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition duration-150 ${
                          isActive
                            ? 'bg-green-50 text-green-700 font-bold'
                            : 'text-gray-700 hover:bg-gray-100 font-medium'
                        }`
                      }
                    >
                      <span className="text-base leading-none">⚙️</span>
                      <span>Account Settings</span>
                    </NavLink>
                  </div>

                  <div className="border-t border-gray-100 my-1"></div>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition duration-150 text-left cursor-pointer"
                  >
                    <span className="text-base leading-none">🚪</span>
                    <span>Logout</span>
                  </button>

                  {/* Regular Website Items - Included in bubble dropdown ONLY on mobile resolution */}
                  <div className="md:hidden">
                    <div className="border-t border-gray-200 my-1.5"></div>
                    <div className="px-4 py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Website Menu
                    </div>
                    <NavLink
                      to="/mental-toughness-book"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                    >
                      Mental Toughness Book
                    </NavLink>
                    <NavLink
                      to="/log-book-hub"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                    >
                      Log Books
                    </NavLink>
                    <NavLink
                      to="/online-match-tracking"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                    >
                      Online Match Tracking
                    </NavLink>
                    <NavLink
                      to="/extras-shop"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                    >
                      Shop
                    </NavLink>
                    <NavLink
                      to="/mental-edge-blog"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                    >
                      Blog
                    </NavLink>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Not logged-in: Show Join button & Hamburger on mobile, Login & Join on desktop */
            <>
              <button
                onClick={() => navigate('/login', { state: { showRegister: true } })}
                className="md:hidden bg-yellow-500 hover:bg-yellow-600 text-green-900 font-bold py-1.5 px-3 rounded-md transition duration-300 text-xs shadow-sm"
              >
                JOIN
              </button>
              <div className="hidden md:flex items-center space-x-3">
                <NavLink
                  to="/login"
                  className="text-white font-medium hover:text-yellow-100 transition duration-300 py-1.5 px-3 rounded"
                >
                  Login
                </NavLink>
                <button
                  onClick={() => navigate('/login', { state: { showRegister: true } })}
                  className="bg-yellow-500 hover:bg-yellow-600 text-green-900 font-bold py-2 px-4 rounded-md transition duration-300 shadow-sm"
                >
                  JOIN FREE
                </button>
              </div>

              {/* Hamburger toggle button for GUEST (not logged in) mobile users */}
              <button
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="md:hidden text-white focus:outline-none p-1.5 rounded hover:bg-white/20 transition"
                aria-label="Toggle navigation menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer menu - ONLY for Guest (Not Logged In) Users */}
      {!isLoggedIn && isMenuOpen && (
        <nav className="md:hidden bg-white text-gray-800 py-3 px-4 border-t border-gray-200 shadow-xl max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col space-y-1">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider px-3 py-1">
              Website
            </div>
            <NavLink
              to="/mental-toughness-book"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Mental Toughness Book
            </NavLink>
            <NavLink
              to="/log-book-hub"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Log Books
            </NavLink>
            <NavLink
              to="/online-match-tracking"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Online Match Tracking
            </NavLink>
            <NavLink
              to="/extras-shop"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Shop
            </NavLink>
            <NavLink
              to="/mental-edge-blog"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Blog
            </NavLink>

            <hr className="my-2 border-gray-200" />
            <NavLink
              to="/login"
              onClick={() => setIsMenuOpen(false)}
              className="py-2 px-3 text-gray-700 hover:bg-gray-50 rounded font-medium"
            >
              Login
            </NavLink>
            <button
              onClick={() => {
                navigate('/login', { state: { showRegister: true } });
                setIsMenuOpen(false);
              }}
              className="w-full text-center bg-yellow-500 hover:bg-yellow-600 text-green-900 font-bold py-2.5 px-4 rounded-md transition duration-300 mt-2"
            >
              JOIN FREE
            </button>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
